import { useState } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { useToast } from '@/context/ToastContext';
import EditPutter from './EditPutter';
import DeletedHistory from '../DeletedHistory';
import { Accordion, ControlledAccordion } from '@/common/Accordion';
import { AccordionShell } from '@/common/Accordion';
import InTheBag from '@/common/InTheBag';
import Tile from '@/common/Tile';
import SpinnerOverlay from '@/common/SpinnerOverlay/SpinnerOverlay';
import TextAreaDisplay from '@/common/TextArea/Display';
import { useEditPutter } from './useEditPutter';
import { TourBagService } from '@/service';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { TourBagMstrPayload, TourBagDetail } from '@/api/models';
import { isEmptyTile, normalizeString, createDeletedTourBag, createEmptyTourBagDetail, createEmptyTourBag } from '@/utils';
import { buildPutterPayload, buildPutterMetadata } from './helpers';
import { isFieldVisible } from '@/library/FieldVisibilityHelper';
import { PageReadability } from '@/library/TourPageSettingsHelper';
import { headTypeMap, Action } from '@/constants';
import clubStyles from '../Club.module.scss';
import { handAbbreviationToFullName } from '@/library/HandHelper';
import ClubHistory from '../ClubHistory/ClubHistory';
import OfflineModal from '@/common/OfflineModal';
import Linkify from 'linkify-react';
import PhotoCaptureSection from '@/common/PhotoCaptureSection';

const emptyTourBagDetail = createEmptyTourBagDetail('Putter');

export interface IPutterClub {
  isActive: boolean;
  putterHand: string;
  putterLevelId: number;
  putterTypeId: number;
  putterHeadModelId: number;
  putterHeadFinishId: number;
  putterSightlineId: number;
  putterFaceId: number;
  putterNeckId: number;
  putterFinalHW: string;
  loft: string;
  lie: string;
  swingWt: string;
  soleWtId: string;
  stamping: string;
  painting: string;
  putterShaftId: number;
  measured: boolean;
  putterShaftLength: string;
  putterGripId: number;
  putterGripTape: string;
  notes: string;
  itemNo: number;
}

const renderPutter = (putter: TourBagDetail, pageReadability: PageReadability | undefined) => {
  const hand = handAbbreviationToFullName(putter.hand);
  const loftValue = `${putter.loftDigit}${putter.loftFraction}`;
  const lieValue = `${putter.lieDigit}${putter.lieFraction}`;
  const shaftName = normalizeString(putter.putterShaftName.trim());
  const gripName = normalizeString(putter.putterGripName);
  const notes = normalizeString(putter.notes);
  const module = 'Putter';

  return (
    <>
      <div className={clubStyles.tileRow}>
        {isFieldVisible(module, 'hand', pageReadability) && <Tile label="Hand" value={hand} />}
        {isFieldVisible(module, 'putterLevelName', pageReadability) && <Tile label="Level" value={normalizeString(putter.putterLevelName)} variant="large" />}
        {isFieldVisible(module, 'putterTypeName', pageReadability) && <Tile label="Type" value={normalizeString(putter.putterTypeName)} variant="large" />}
        {isFieldVisible(module, 'headFinishName', pageReadability) && <Tile label="Finish" value={normalizeString(putter.headFinishName)} variant="large" />}
        {isFieldVisible(module, 'putterSightlineName', pageReadability) && <Tile label="Sightline" value={normalizeString(putter.putterSightlineName)} variant="large" />}
        {isFieldVisible(module, 'putterFaceName', pageReadability) && <Tile label="Face" value={normalizeString(putter.putterFaceName)} variant="large" />}
        {isFieldVisible(module, 'putterNeckName', pageReadability) && <Tile label="Neck" value={normalizeString(putter.putterNeckName)} variant="large" />}
      </div>
      {(isFieldVisible(module, 'loft', pageReadability) ||
        isFieldVisible(module, 'lie', pageReadability) ||
        isFieldVisible(module, 'swingWt', pageReadability) ||
        isFieldVisible(module, 'putterWeightName', pageReadability) ||
        isFieldVisible(module, 'putterFinalHW', pageReadability)) && (
        <div className={clubStyles.tileRow}>
          {isFieldVisible(module, 'loft', pageReadability) && <Tile label="Loft" value={normalizeString(loftValue)} />}
          {isFieldVisible(module, 'lie', pageReadability) && <Tile label="Lie" value={normalizeString(lieValue)} />}
          {isFieldVisible(module, 'swingWt', pageReadability) && <Tile label="Swing Wt." value={normalizeString(`${putter.swingWtPrefixName} ${putter.swingWtSuffixName}`)} />}
          {isFieldVisible(module, 'putterWeightName', pageReadability) && <Tile label="Sole Wts." value={normalizeString(putter.putterWeightName)} />}
          {isFieldVisible(module, 'putterFinalHW', pageReadability) && <Tile label="Overall Wt." value={normalizeString(putter.putterFinalHW ? putter.putterFinalHW : '')} />}
        </div>
      )}
      {(isFieldVisible(module, 'stamping', pageReadability) || isFieldVisible(module, 'paintingNotes', pageReadability)) && (
        <div className={clubStyles.tileRow}>
          <div className={clubStyles.textAreaGroup}>
            {isFieldVisible(module, 'stamping', pageReadability) && <TextAreaDisplay value={normalizeString(putter.stamping)} label="Stamping" />}
            {isFieldVisible(module, 'paintingNotes', pageReadability) && <TextAreaDisplay value={normalizeString(putter.paintingNotes)} label="Painting" />}
          </div>
        </div>
      )}
      {isFieldVisible(module, 'putterShaftName', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Shaft</div>
            <div className={`${clubStyles.categoryTitle} ${isEmptyTile(shaftName) ? clubStyles.emptyValue : ''}`}>{shaftName}</div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'isShaftLengthFinished', pageReadability) && <Tile label="Measured" value={putter.isShaftLengthFinished ? 'Finished' : 'Cut'} />}
            {isFieldVisible(module, 'shaftLengthDigit', pageReadability) && <Tile label="Length" value={normalizeString(`${putter.shaftLengthDigit} ${putter.shaftLengthFraction}`)} />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'putterGripName', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Grip</div>
            <div className={`${clubStyles.categoryTitle} ${isEmptyTile(gripName) ? clubStyles.emptyValue : ''}`}>{gripName}</div>
          </div>
          <div className={clubStyles.tileRow}>{isFieldVisible(module, 'tape', pageReadability) && <Tile label="Grip Tape" value={normalizeString(putter.tape)} variant="large" />}</div>
        </>
      )}
      {isFieldVisible(module, 'notes', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Notes</div>
          </div>
          <div className={clubStyles.tileRow}>
            <div className={`${clubStyles.notes} ${isEmptyTile(notes) ? clubStyles.emptyValue : ''}`}>
              <Linkify options={{ target: '_blank' }}>{notes}</Linkify>
            </div>
          </div>
        </>
      )}
      <PhotoCaptureSection playerId={putter.playerId} ownerRowGuid={putter.rowGuid} headTypeId={putter.headTypeId} imageMetadata={putter.imageMetadata} />
    </>
  );
};

interface IPutterReadOnlyProps {
  putterClubs: TourBagDetail[];
  currentAccordionId: number;
  showCheckbox: boolean;
  /* eslint-disable no-unused-vars */
  isClubSelected: (id: number) => boolean;
  handleOpenAccordion: (id: number) => void;
  handleCheckboxEvent: (id: number, isChecked: boolean) => void;
  pageReadability?: PageReadability | undefined;
  /* eslint-enable no-unused-vars */
}

export const PutterReadOnly = ({ putterClubs, currentAccordionId, showCheckbox, isClubSelected, handleOpenAccordion, handleCheckboxEvent, pageReadability }: IPutterReadOnlyProps) => {
  return (
    <>
      {putterClubs
        ?.sort((a, b) => a.headModelName.localeCompare(b.headModelName))
        ?.sort((a, b) => Number(b.isActive) - Number(a.isActive))
        .map((putter) => {
          return (
            <ControlledAccordion
              key={putter.tourBagDetailId}
              id={putter.tourBagDetailId}
              header={
                <>
                  <span className={clubStyles.model}>{pageReadability === 'limitedView' && putter.headModelAlternateName ? putter.headModelAlternateName : putter.headModelName}</span>
                  <InTheBag isActive={putter.isActive} className={clubStyles.inTheBag} />
                </>
              }
              isOpen={currentAccordionId === putter.tourBagDetailId}
              showCheckbox={showCheckbox}
              checked={isClubSelected(putter.tourBagDetailId)}
              handleOpenAccordion={() => handleOpenAccordion(putter.tourBagDetailId)}
              handleCheckboxEvent={handleCheckboxEvent}
            >
              {renderPutter(putter, pageReadability)}
            </ControlledAccordion>
          );
        })}
    </>
  );
};

function Putter() {
  const { tourBag, player, updateTourBag, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const pageReadability = tourPageSettings?.putter;
  const { isSyncComplete } = useBackgroundSync();
  const { showToast } = useToast();
  const isOnline = useOnlineStatus();
  const [showSpinner, setShowSpinner] = useState(false);
  const [putterToEdit, setPutterToEdit] = useState<TourBagDetail | null>(null);
  const [putterToEditCached, setPutterToEditCached] = useState<TourBagDetail | null>(null);
  const [putterToCreate, setPutterToCreate] = useState<TourBagDetail | null>(null);
  const [showCreatePutter, setShowCreatePutter] = useState<boolean>(false);
  const [showDeletedHistory, setShowDeletedHistory] = useState(false);
  const putterBag = tourBag?.find((bag) => bag.headTypeDescription === 'Putter');
  const putterClubs = putterBag?.tourBagDetail;
  const showPutterList = !putterToEdit && !putterToCreate && !showCreatePutter;
  const [putterForHistory, setPutterForHistory] = useState<TourBagDetail | null>(null);
  const [showClubHistory, setShowClubHistory] = useState(false);
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);

  const { handDropdownData, loftDropdownData, lieDropdownData, putterStaticDropdownData, putterDynamicDropdownData, shaftLengthDropdownData, swingWtDropdownData } = useEditPutter();

  const handleAddNewPutter = async (putterClub: IPutterClub) => {
    const tourBag = putterBag ?? createEmptyTourBag('Putter', player!);
    if (isOnline) {
      setShowSpinner(true);
    }

    //determine itemNo as max of other clubs
    let itemNo = 1;
    if (tourBag.tourBagDetail.length > 0) {
      itemNo = Math.max(...tourBag.tourBagDetail.map((tb) => tb.itemNo)) + 1;
    }
    putterClub.itemNo = itemNo;

    // build new putter writing the new values over the default empty bag detail
    const newPutter: TourBagDetail = {
      ...emptyTourBagDetail,
      ...buildPutterMetadata({ player }),
      ...buildPutterPayload({
        putterClub,
        loftDropdownData,
        lieDropdownData,
        putterStaticDropdownData,
        putterDynamicDropdownData,
        shaftLengthDropdownData,
        swingWtDropdownData,
      }),
      tourBagMstrId: putterBag?.tourBagMstrId || emptyTourBagDetail.tourBagMstrId,
    };
    const updatedPutters: TourBagDetail[] = [...(putterClubs || []), newPutter];
    const updatedBag: TourBagMstrPayload = {
      ...tourBag,
      tourBagDetail: updatedPutters,
    };

    try {
      const action: Action = 'CREATE';
      await updateTourBag(updatedBag, action, newPutter.tourBagDetailId);
      setPutterToCreate(null); // exit creation upon success
      setShowCreatePutter(false); // hide add new putter component
    } catch (error) {
      showToast({ message: 'Failed to Create Putter' });
      console.error('Failed to Create Putter club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        setShowSpinner(false);
        showToast({ message: 'Putter Created Successfully', showCheckmark: true });
      }
    }
  };

  const handleEditedPutter = async (putterClub: IPutterClub) => {
    if (!putterClubs) throw new Error('Missing putter clubs');
    if (isOnline) {
      setShowSpinner(true);
    }

    const updatedPutters: TourBagDetail[] = putterClubs.map((putter) => {
      if (putterToEdit?.tourBagDetailId === putter.tourBagDetailId) {
        const updatedPutter = buildPutterPayload({
          putterClub,
          loftDropdownData,
          lieDropdownData,
          putterStaticDropdownData,
          putterDynamicDropdownData,
          shaftLengthDropdownData,
          swingWtDropdownData,
        });
        return { ...putter, ...updatedPutter };
      }
      return putter;
    });
    const updatedBag: TourBagMstrPayload = {
      ...putterBag,
      tourBagDetail: updatedPutters,
    };

    try {
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, putterToEdit?.tourBagDetailId);
      setPutterToEdit(null); // exit edition upon success
    } catch (error) {
      showToast({ message: 'Failed to Edit Putter' });
      console.error('Failed to Edit Putter club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        showToast({ message: 'Putter Edited Successfully', showCheckmark: true });
        setShowSpinner(false);
      }
    }
  };

  const handleDeletePutter = async (tourBagDetailId: number) => {
    if (!putterClubs) throw new Error('Missing Putter clubs');

    const tourBag = putterClubs.find((putter) => putter.tourBagDetailId === tourBagDetailId);
    if (!tourBag) throw new Error('Missing Putter bag to delete');
    setShowSpinner(true);

    const deletedBag = createDeletedTourBag(tourBag);
    const updatedPutters = putterClubs.filter((putter) => putter.tourBagDetailId !== tourBagDetailId);
    const updatedBag: TourBagMstrPayload = {
      ...putterBag,
      tourBagDetail: updatedPutters,
    };

    try {
      await TourBagService.saveDeletedTourBag(deletedBag);
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, tourBagDetailId);
      showToast({ message: 'Putter Deleted Successfully', showCheckmark: true });
    } catch (error) {
      showToast({ message: 'Failed to Delete Putter' });
      console.error('Failed to Delete Putter club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      setShowSpinner(false);
    }
  };

  const handleHistory = (putter: TourBagDetail) => {
    setPutterForHistory(putter);
    setShowClubHistory(true);
  };

  const closeClubHistoryModal = () => {
    setShowClubHistory(false);
  };

  const openOfflineEditModal = (putter: TourBagDetail | null) => {
    setPutterToEditCached(putter);
    setShowPreEditOfflineModal(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    //edit the putter if not null, otherwise this is an add new
    if (putterToEditCached) {
      setPutterToEdit({ ...putterToEditCached });
      setPutterToEditCached(null);
    } else {
      setShowCreatePutter(true);
    }
  };

  return (
    <div className={`${clubStyles.container} ${putterToEdit !== null || showCreatePutter ? clubStyles.editingBackground : ''}`}>
      {!putterClubs?.length && showPutterList && <div className={clubStyles.noClubs}>No Putters in System</div>}
      {(putterToCreate || showCreatePutter) && (
        <AccordionShell>
          <EditPutter
            title="Create Club"
            putter={putterToCreate || emptyTourBagDetail}
            handDropdownData={handDropdownData}
            loftDropdownData={loftDropdownData}
            lieDropdownData={lieDropdownData}
            putterStaticDropdownData={putterStaticDropdownData}
            putterDynamicDropdownData={putterDynamicDropdownData}
            shaftLengthDropdownData={shaftLengthDropdownData}
            swingWtDropdownData={swingWtDropdownData}
            handleSave={handleAddNewPutter}
            handleCancel={() => {
              setPutterToCreate(null);
              setShowCreatePutter(false);
            }}
          />
        </AccordionShell>
      )}
      {putterToEdit !== null && (
        <AccordionShell>
          <EditPutter
            title="Edit Club"
            putter={putterToEdit}
            handDropdownData={handDropdownData}
            loftDropdownData={loftDropdownData}
            lieDropdownData={lieDropdownData}
            putterStaticDropdownData={putterStaticDropdownData}
            putterDynamicDropdownData={putterDynamicDropdownData}
            shaftLengthDropdownData={shaftLengthDropdownData}
            swingWtDropdownData={swingWtDropdownData}
            handleSave={handleEditedPutter}
            handleCancel={() => setPutterToEdit(null)}
          />
        </AccordionShell>
      )}
      {showPutterList &&
        putterClubs
          ?.sort((a, b) => a.headModelName.localeCompare(b.headModelName))
          ?.sort((a, b) => Number(b.isActive) - Number(a.isActive))
          .map((putter, index) => {
            return (
              <Accordion
                key={putter.tourBagDetailId}
                index={index}
                header={
                  <>
                    <span className={clubStyles.model}>{pageReadability === 'limitedView' && putter.headModelAlternateName ? putter.headModelAlternateName : putter.headModelName}</span>
                    <InTheBag isActive={putter.isActive} className={clubStyles.inTheBag} />
                  </>
                }
                showMenu={tourPageSettings?.putter === 'full' && isSyncComplete}
                showHistory={permissions?.tourBagSettings?.clubHistory ?? false}
                handleEdit={!isOnline ? () => openOfflineEditModal(putter) : () => setPutterToEdit(putter)}
                handleCopy={() => setPutterToCreate(putter)}
                handleDelete={() => handleDeletePutter(putter.tourBagDetailId)}
                handleHistory={() => handleHistory(putter)}
              >
                {/* View Putter */}
                {renderPutter(putter, pageReadability)}
              </Accordion>
            );
          })}
      {showPutterList && tourPageSettings?.putter === 'full' && isSyncComplete && (
        <div className={`${clubStyles.addNewWrapper} ${clubStyles.buttonGroup}`}>
          <button className={clubStyles.addNew} onClick={!isOnline ? () => openOfflineEditModal(null) : () => setShowCreatePutter(true)} aria-label="Add New Putter">
            + Add New
          </button>
          {isOnline && permissions?.tourBagSettings?.clubDeleted && (
            <button className={clubStyles.addNew} onClick={() => setShowDeletedHistory(true)} aria-label="Show Deleted Putters">
              Show Deleted
            </button>
          )}
        </div>
      )}
      {showDeletedHistory && (
        <DeletedHistory
          headTypeId={headTypeMap['Putter']}
          playerId={putterBag?.playerId || 0}
          playerName={putterBag?.playerName || ''}
          showDeletedHistory={showDeletedHistory}
          closeDeletedHistory={() => setShowDeletedHistory(false)}
        />
      )}
      {showClubHistory && (
        <ClubHistory
          tourBagMstrId={putterForHistory?.tourBagMstrId || 0}
          rowGuid={putterForHistory?.rowGuid || ''}
          headTypeId={headTypeMap['Putter']}
          player={player}
          model={putterForHistory?.headModelName || ''}
          closeModal={closeClubHistoryModal}
        />
      )}
      {showSpinner && <SpinnerOverlay />}
      <OfflineModal onCancel={() => setShowPreEditOfflineModal(false)} onClose={showEditFormOfflineModal} isOpen={showPreEditOfflineModal} modalType={'IsOffline'} />
      <OfflineModal onCancel={() => {}} onClose={() => setShowPostEditOfflineModal(false)} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </div>
  );
}

export default Putter;
